/**
 * MinimizedSection - promote a todo widget into the secondary panel.
 *
 * Same geometry idle and active (no height swap). Active = primary wash
 * (`SETTINGS_SELECTED_SURFACE_CLASS` / gallery selected row). Color + chevron
 * ease-in-out only.
 */

import { ChevronRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { MinimizedSectionProps } from './secondary-panel-types';
import { cn } from '@/lib/utils';

export function MinimizedSection({
  type,
  title,
  count,
  preview,
  onPromote,
  onDemote,
  isPromoted,
  'data-testid': dataTestId,
}: MinimizedSectionProps) {
  return (
    <button
      type="button"
      data-testid={dataTestId ?? `basecamp-todo-widget-${type}`}
      data-section={type}
      aria-pressed={isPromoted}
      onClick={isPromoted ? onDemote : onPromote}
      className={cn(
        'w-full rounded-lg border border-l-2 p-3 text-left',
        'transition-colors duration-200 ease-in-out',
        isPromoted
          ? 'border-primary/35 border-l-primary bg-primary/15 hover:bg-primary/20'
          : 'border-border border-l-border bg-card hover:bg-muted/50',
      )}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <h3
            className={cn(
              'text-sm font-semibold uppercase tracking-wider transition-colors duration-200 ease-in-out',
              isPromoted ? 'text-primary' : 'text-muted-foreground',
            )}
          >
            {title}
          </h3>
          <Badge variant="secondary" className="text-xs font-normal">
            {count}
          </Badge>
        </div>
      </div>

      {preview ? <div className="mb-2">{preview}</div> : null}

      <div className="mt-2 flex w-full items-center justify-between border-t border-border/80 pt-2 text-xs text-muted-foreground">
        <span>View all {title.toLowerCase()}</span>
        <ChevronRight
          className={cn(
            'h-3 w-3 transition-transform duration-200 ease-in-out',
            isPromoted && 'rotate-180',
          )}
          aria-hidden
        />
      </div>
    </button>
  );
}
